import { z } from "zod"

import {
  deviceInstallationIdSchema,
  deviceSessionIdSchema,
  identityIdSchema,
  membershipIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const hostnameSchema = z
  .string()
  .min(1)
  .max(253)
  .regex(
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/,
  )

const secretSchema = z.string().min(32).max(512)
const accessTokenSchema = z.string().min(32).max(4096)

export const fieldTenantChoiceV1Schema = z
  .object({
    membershipId: membershipIdSchema,
    tenantId: tenantIdSchema,
    name: z.string().min(1).max(80),
    location: z.string().min(1).max(80),
  })
  .strict()

export const fieldDeviceSessionSummaryV1Schema = z
  .object({
    deviceSessionId: deviceSessionIdSchema,
    deviceLabel: z.string().min(1).max(80),
    selectedMembershipId: membershipIdSchema,
    selectedTenantId: tenantIdSchema,
    status: z.enum(["active", "revoked"]),
    createdAt: z.string().datetime({ offset: true }),
    lastSeenAt: z.string().datetime({ offset: true }),
  })
  .strict()

export const fieldSessionCredentialV1Schema = z
  .object({
    sessionVersion: z.literal(1),
    deviceSessionId: deviceSessionIdSchema,
    selectedIdentityId: identityIdSchema,
    selectedMembershipId: membershipIdSchema,
    selectedTenantId: tenantIdSchema,
    tenants: z.array(fieldTenantChoiceV1Schema).min(1).max(20),
    accessToken: accessTokenSchema,
    accessTokenExpiresAt: z.number().int().positive(),
    refreshToken: secretSchema,
  })
  .strict()
  .superRefine((session, context) => {
    if (
      !session.tenants.some(
        (tenant) =>
          tenant.membershipId === session.selectedMembershipId &&
          tenant.tenantId === session.selectedTenantId,
      )
    ) {
      context.addIssue({
        code: "custom",
        path: ["selectedTenantId"],
        message: "Selected Tenant and Membership must be available.",
      })
    }
  })

export const fieldOidcBeginRequestV1Schema = z
  .object({
    brandHostname: hostnameSchema,
  })
  .strict()

export const fieldOidcBeginResponseV1Schema = z
  .object({
    responseVersion: z.literal(1),
    authorizationUrl: z.string().url().refine((value) => value.startsWith("https://")),
    redirectUri: z.literal("impetusos-field://auth/callback"),
  })
  .strict()

const deviceInputSchema = z
  .object({
    deviceInstallationId: deviceInstallationIdSchema,
    deviceLabel: z.string().trim().min(1).max(80),
  })
  .strict()

export const fieldOidcCompleteRequestV1Schema = deviceInputSchema
  .extend({
    brandHostname: hostnameSchema,
    state: secretSchema,
    code: z.string().min(1).max(4096),
    membershipId: membershipIdSchema.optional(),
    tenantId: tenantIdSchema.optional(),
  })
  .strict()
  .refine(
    (request) =>
      (request.membershipId === undefined) === (request.tenantId === undefined),
    {
      message: "Membership and Tenant selection must be supplied together.",
      path: ["membershipId"],
    },
  )

export const fieldSessionRefreshRequestV1Schema = z
  .object({
    deviceSessionId: deviceSessionIdSchema,
    deviceInstallationId: deviceInstallationIdSchema,
    refreshToken: secretSchema,
  })
  .strict()

export const fieldSessionSwitchRequestV1Schema =
  fieldSessionRefreshRequestV1Schema
    .extend({
      membershipId: membershipIdSchema,
      tenantId: tenantIdSchema,
    })
    .strict()

export const fieldSessionRevokeRequestV1Schema = z
  .object({
    targetDeviceSessionId: deviceSessionIdSchema,
  })
  .strict()

export const fieldDeviceSessionListV1Schema = z
  .object({
    responseVersion: z.literal(1),
    sessions: z.array(fieldDeviceSessionSummaryV1Schema).max(20),
  })
  .strict()

export type FieldTenantChoiceV1 = z.infer<typeof fieldTenantChoiceV1Schema>
export type FieldDeviceSessionSummaryV1 = z.infer<
  typeof fieldDeviceSessionSummaryV1Schema
>
export type FieldSessionCredentialV1 = z.infer<
  typeof fieldSessionCredentialV1Schema
>
export type FieldOidcBeginRequestV1 = z.infer<
  typeof fieldOidcBeginRequestV1Schema
>
export type FieldOidcBeginResponseV1 = z.infer<
  typeof fieldOidcBeginResponseV1Schema
>
export type FieldOidcCompleteRequestV1 = z.infer<
  typeof fieldOidcCompleteRequestV1Schema
>
export type FieldSessionRefreshRequestV1 = z.infer<
  typeof fieldSessionRefreshRequestV1Schema
>
export type FieldSessionSwitchRequestV1 = z.infer<
  typeof fieldSessionSwitchRequestV1Schema
>
export type FieldSessionRevokeRequestV1 = z.infer<
  typeof fieldSessionRevokeRequestV1Schema
>
export type FieldDeviceSessionListV1 = z.infer<
  typeof fieldDeviceSessionListV1Schema
>
